import { useState, useEffect } from "react";
import { apiClient, useAuth } from "../../App";
import { History, Search, User, Shield } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ScrollArea } from "../../components/ui/scroll-area";

const ACTION_COLORS = {
  create: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  update: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  delete: "bg-red-500/10 text-red-600 border-red-500/20",
  approve: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  disable: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  enable: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
};

function formatTime(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  if (diff < 60000) return "Just now";
  if (diff < 3600000) return Math.floor(diff / 60000) + "m ago";
  if (diff < 86400000) return Math.floor(diff / 3600000) + "h ago";
  return date.toLocaleDateString();
}

function LogEntry({ log }) {
  const isUser = log.type === "user";
  const isRole = log.type === "role";
  return (
    <div className="p-4 hover:bg-muted/30 transition-colors">
      <div className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
          {isUser ? <User className="w-4 h-4 text-muted-foreground" /> :
           isRole ? <Shield className="w-4 h-4 text-muted-foreground" /> :
           <History className="w-4 h-4 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{log.actor_name}</span>
            <Badge variant="outline" className={ACTION_COLORS[log.action] || ""}>{log.action}</Badge>
            <span className="text-muted-foreground">{log.target}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{log.details}</p>
        </div>
        <span className="text-xs text-muted-foreground shrink-0">{formatTime(log.timestamp)}</span>
      </div>
    </div>
  );
}

export default function AuditLog() {
  const { user: currentUser } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => { fetchLogs(); }, []);

  async function fetchLogs() {
    try {
      const res = await apiClient.get("/admin/audit-log");
      setLogs(res.data.logs || []);
    } catch (e) {
      // Use sample data if API fails
      setLogs([
        { id: "1", action: "update", type: "permission", actor_name: "Bernard", target: "Program Manager role", details: "Changed can_view_costing to false", timestamp: new Date().toISOString() },
        { id: "2", action: "approve", type: "user", actor_name: "Bernard", target: "Julian Ruiz", details: "Approved user account", timestamp: new Date(Date.now() - 3600000).toISOString() },
      ]);
    }
    finally { setLoading(false); }
  }

  const filteredLogs = logs.filter(log => {
    const matchSearch = !search || log.actor_name?.toLowerCase().includes(search.toLowerCase()) || log.target?.toLowerCase().includes(search.toLowerCase());
    const matchAction = actionFilter === "all" || log.action === actionFilter;
    const matchType = typeFilter === "all" || log.type === typeFilter;
    return matchSearch && matchAction && matchType;
  });

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-light tracking-tight">Audit Log</h1>
        <p className="text-sm text-muted-foreground mt-1">Track all permission and user changes</p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Actions" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="approve">Approve</SelectItem>
                <SelectItem value="disable">Disable</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="role">Role</SelectItem>
                <SelectItem value="permission">Permission</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center text-muted-foreground">Loading...</div> :
           filteredLogs.length === 0 ? <div className="p-8 text-center text-muted-foreground">No logs found</div> :
           <ScrollArea className="h-[500px]">
             <div className="divide-y">
               {filteredLogs.map(log => <LogEntry key={log.id} log={log} />)}
             </div>
           </ScrollArea>
          }
        </CardContent>
      </Card>
    </div>
  );
}
